// ===============================================================
// V·IANet Requests (M16) — FormulariosTab: Editor de JSON-schema
// Lista campos reordenable + add/edit/delete + preview
// ===============================================================

import { useState, useMemo } from 'react';
import { useI18n } from '../../../../i18n';
import { useRequestsConfig } from '../hooks/useRequestsConfig';
import type { FormularioSchema, TipoSolicitud } from '../types/requests.types';
import { FormSchemaRenderer, type FormComponent } from './FormSchemaRenderer';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';

const FIELD_TYPES = [
  { value: 'text', label: 'Texto' },
  { value: 'select', label: 'Seleccion' },
  { value: 'date', label: 'Fecha' },
  { value: 'datetime', label: 'Fecha y hora' },
  { value: 'currency', label: 'Moneda' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Opciones (radio)' },
  { value: 'lookup', label: 'Lookup' },
  { value: 'file', label: 'Archivo' },
];

interface FieldForm {
  key: string;
  label: string;
  type: string;
  required: boolean;
  options: string;
  multiline: boolean;
}

const EMPTY_FIELD: FieldForm = {
  key: '',
  label: '',
  type: 'text',
  required: false,
  options: '',
  multiline: false,
};

export function FormulariosTab() {
  const { t } = useI18n();
  const {
    items: formularios,
    loading,
    actualizar,
  } = useRequestsConfig<FormularioSchema>('/requests/formularios');
  const { items: tipos } = useRequestsConfig<TipoSolicitud>('/requests/tipos');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [components, setComponents] = useState<FormComponent[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [fieldForm, setFieldForm] = useState<FieldForm>(EMPTY_FIELD);
  const [showPreview, setShowPreview] = useState(false);
  const [previewValues, setPreviewValues] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const selectedFormulario = formularios.find((f) => f.id === selectedId);
  const tipoName = (tipoId: string) => tipos.find((t) => t.id === tipoId)?.nombre ?? tipoId;

  const selectFormulario = (f: FormularioSchema) => {
    setSelectedId(f.id);
    const schema = f.schema as { components?: FormComponent[] };
    setComponents(schema?.components ?? []);
    setShowPreview(false);
  };

  const openAddField = () => {
    setEditIndex(null);
    setFieldForm(EMPTY_FIELD);
    setShowModal(true);
  };

  const openEditField = (index: number) => {
    const c = components[index];
    setEditIndex(index);
    setFieldForm({
      key: c.key,
      label: c.label,
      type: c.type,
      required: c.validate?.required ?? false,
      options: c.options?.map((o) => o.label).join(', ') ?? '',
      multiline: !!(c.attrs as Record<string, unknown>)?.multiline,
    });
    setShowModal(true);
  };

  const handleSaveField = () => {
    const comp: FormComponent = {
      type: fieldForm.type,
      key: fieldForm.key || fieldForm.label.toLowerCase().replace(/\s+/g, '_'),
      label: fieldForm.label,
      validate: fieldForm.required ? { required: true } : undefined,
      options:
        fieldForm.type === 'select' || fieldForm.type === 'radio'
          ? fieldForm.options
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
              .map((s) => ({ value: s.toLowerCase().replace(/\s+/g, '_'), label: s }))
          : undefined,
      attrs: fieldForm.multiline ? { multiline: true } : undefined,
    };

    setComponents((prev) => {
      if (editIndex !== null) {
        const next = [...prev];
        next[editIndex] = comp;
        return next;
      }
      return [...prev, comp];
    });
    setShowModal(false);
  };

  const deleteField = (index: number) => {
    setComponents((prev) => prev.filter((_, i) => i !== index));
  };

  const moveField = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= components.length) return;
    setComponents((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSaveSchema = async () => {
    if (!selectedId) return;
    setSaving(true);
    setSaved(false);
    try {
      await actualizar(selectedId, {
        schema: { components },
      } as Partial<FormularioSchema>);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // error
    } finally {
      setSaving(false);
    }
  };

  const previewSchema = useMemo(() => ({ components }), [components]);

  if (loading) {
    return <LoadingSpinner size="md" muted />;
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
          {t.requests?.admin?.formularios ?? 'Editor de Formularios'}
        </h3>
      </div>

      {/* Formulario selector */}
      {!selectedId ? (
        <div>
          {formularios.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>
              {t.requests?.sinDatos ?? 'No hay formularios. Crea un Tipo de Solicitud primero.'}
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 12,
              }}
            >
              {formularios.map((f) => (
                <button
                  key={f.id}
                  onClick={() => selectFormulario(f)}
                  style={{
                    textAlign: 'left',
                    padding: 16,
                    borderRadius: 10,
                    border: '1px solid #E5E7EB',
                    background: '#fff',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLElement).style.borderColor = '#33BCE1')
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLElement).style.borderColor = '#E5E7EB')
                  }
                >
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1F3864' }}>
                    {tipoName(f.tipoSolicitudId)}
                  </div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                    v{f.version} &middot;{' '}
                    {(f.schema as { components?: unknown[] })?.components?.length ?? 0} campos
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Toolbar */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginBottom: 16,
              flexWrap: 'wrap',
            }}
          >
            <button
              onClick={() => setSelectedId(null)}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: '1px solid #D1D5DB',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              &larr; {t.requests?.cancelar ?? 'Volver'}
            </button>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1F3864', flex: 1 }}>
              {selectedFormulario ? tipoName(selectedFormulario.tipoSolicitudId) : 'Formulario'}
            </span>
            <button
              onClick={() => setShowPreview(!showPreview)}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: '1px solid #D1D5DB',
                background: showPreview ? '#EFF6FF' : '#fff',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {showPreview ? 'Editor' : 'Preview'}
            </button>
            <button
              onClick={openAddField}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: 'none',
                background: '#2F5496',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              + Campo
            </button>
            <button
              onClick={handleSaveSchema}
              disabled={saving}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: 'none',
                background: saving ? '#9CA3AF' : '#1F3864',
                color: '#fff',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {saving ? 'Guardando...' : saved ? 'Guardado' : t.common.guardar}
            </button>
          </div>

          {showPreview ? (
            /* Preview */
            <div
              style={{
                background: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: 10,
                padding: 24,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#6B7280',
                  marginBottom: 16,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Vista previa del formulario
              </div>
              {components.length === 0 ? (
                <div style={{ color: '#9CA3AF', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  Sin campos
                </div>
              ) : (
                <FormSchemaRenderer
                  schema={previewSchema}
                  values={previewValues}
                  onChange={(key, value) => setPreviewValues((prev) => ({ ...prev, [key]: value }))}
                />
              )}
            </div>
          ) : (
            /* Field list */
            <div>
              {components.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: 48,
                    color: '#9CA3AF',
                    background: '#FAFBFC',
                    borderRadius: 10,
                    border: '1px dashed #D1D5DB',
                  }}
                >
                  Sin campos. Pulsa "+ Campo" para agregar.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                      <th style={TH}>#</th>
                      <th style={{ ...TH, textAlign: 'left' }}>Clave</th>
                      <th style={{ ...TH, textAlign: 'left' }}>Etiqueta</th>
                      <th style={TH}>Tipo</th>
                      <th style={TH}>Obligatorio</th>
                      <th style={TH}>Orden</th>
                      <th style={{ ...TH, textAlign: 'right' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {components.map((c, i) => (
                      <tr key={`${c.key}-${i}`} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={TD_CENTER}>{i + 1}</td>
                        <td style={TD}>
                          <code style={{ fontSize: 12, color: '#6B7280' }}>{c.key}</code>
                        </td>
                        <td style={TD}>{c.label}</td>
                        <td style={TD_CENTER}>
                          <span
                            style={{
                              fontSize: 11,
                              padding: '2px 8px',
                              borderRadius: 4,
                              background: '#EFF6FF',
                              color: '#2F5496',
                              fontWeight: 600,
                            }}
                          >
                            {c.type}
                          </span>
                        </td>
                        <td style={TD_CENTER}>{c.validate?.required ? 'Si' : '-'}</td>
                        <td style={TD_CENTER}>
                          <button
                            onClick={() => moveField(i, -1)}
                            disabled={i === 0}
                            style={ARROW_BTN}
                          >
                            &#9650;
                          </button>
                          <button
                            onClick={() => moveField(i, 1)}
                            disabled={i === components.length - 1}
                            style={ARROW_BTN}
                          >
                            &#9660;
                          </button>
                        </td>
                        <td style={{ ...TD, textAlign: 'right' }}>
                          <button onClick={() => openEditField(i)} title="Editar" style={ICON_BTN}>
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z"
                                stroke="#2F5496"
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          <button onClick={() => deleteField(i)} title="Eliminar" style={ICON_BTN}>
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M3 4h10M5.5 4V3a1 1 0 011-1h3a1 1 0 011 1v1M6 7v5M10 7v5M4 4l.7 9a1 1 0 001 .9h4.6a1 1 0 001-.9L12 4"
                                stroke="#DC2626"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Field Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              width: 480,
              maxHeight: '80vh',
              overflow: 'auto',
            }}
          >
            <h4 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
              {editIndex !== null ? 'Editar campo' : 'Nuevo campo'}
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <label style={LABEL}>
                Etiqueta *
                <input
                  value={fieldForm.label}
                  onChange={(e) => setFieldForm((f) => ({ ...f, label: e.target.value }))}
                  style={INPUT}
                />
              </label>

              <label style={LABEL}>
                Clave (auto si vacio)
                <input
                  value={fieldForm.key}
                  onChange={(e) => setFieldForm((f) => ({ ...f, key: e.target.value }))}
                  placeholder={fieldForm.label.toLowerCase().replace(/\s+/g, '_') || 'campo_nuevo'}
                  style={INPUT}
                />
              </label>

              <label style={LABEL}>
                Tipo
                <select
                  value={fieldForm.type}
                  onChange={(e) => setFieldForm((f) => ({ ...f, type: e.target.value }))}
                  style={{ ...INPUT, cursor: 'pointer' }}
                >
                  {FIELD_TYPES.map((ft) => (
                    <option key={ft.value} value={ft.value}>
                      {ft.label}
                    </option>
                  ))}
                </select>
              </label>

              {(fieldForm.type === 'select' || fieldForm.type === 'radio') && (
                <label style={LABEL}>
                  Opciones (separadas por coma)
                  <input
                    value={fieldForm.options}
                    onChange={(e) => setFieldForm((f) => ({ ...f, options: e.target.value }))}
                    placeholder="Opcion 1, Opcion 2, Opcion 3"
                    style={INPUT}
                  />
                </label>
              )}

              {fieldForm.type === 'text' && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    color: '#374151',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={fieldForm.multiline}
                    onChange={(e) => setFieldForm((f) => ({ ...f, multiline: e.target.checked }))}
                    style={{ width: 16, height: 16, accentColor: '#2F5496' }}
                  />
                  Multilinea (textarea)
                </label>
              )}

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: '#374151',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={fieldForm.required}
                  onChange={(e) => setFieldForm((f) => ({ ...f, required: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: '#2F5496' }}
                />
                Obligatorio
              </label>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.cancelar ?? 'Cancelar'}
              </button>
              <button
                onClick={handleSaveField}
                disabled={!fieldForm.label.trim()}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: fieldForm.label.trim() ? '#1F3864' : '#9CA3AF',
                  color: '#fff',
                  cursor: fieldForm.label.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {t.common.guardar}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TH: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 12,
  fontWeight: 600,
  color: '#6B7280',
  textAlign: 'center',
};

const TD: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 13,
};

const TD_CENTER: React.CSSProperties = {
  ...TD,
  textAlign: 'center',
};

const LABEL: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  display: 'block',
};

const INPUT: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 4,
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #D1D5DB',
  fontSize: 14,
  boxSizing: 'border-box',
};

const ICON_BTN: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  marginLeft: 4,
};

const ARROW_BTN: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '0 2px',
  fontSize: 10,
  color: '#6B7280',
};
